const REST_BASE = window.eyfs?.rest_url || '/wp-json/eyfs/v1'
const AJAX_BASE = window.eyfs?.ajaxurl || '/wp-admin/admin-ajax.php'
const NONCE = window.eyfs?.nonce || ''

const COOKIE_BASE_NAME = 'ey_resources_filters'
const COOKIE_EXPIRY_DAYS = 30

// Generate a unique cookie name for each block instance
const generateCookieName = (blockId) => {
  return `${COOKIE_BASE_NAME}_${blockId}`
}

// Generate a unique block ID based on current URL path
export const generateBlockId = () => {
  if (typeof window === 'undefined') return 'default'

  // Get the current path without domain
  const path = window.location.pathname

  // Create a simple hash from the path
  let hash = 0
  for (let i = 0; i < path.length; i++) {
    const char = path.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }

  // Convert to positive hex string and take first 8 characters
  return Math.abs(hash).toString(16).substring(0, 8)
}

export const saveFiltersToCookie = (filters, blockId) => {
  try {
    const cookieValue = JSON.stringify(filters)
    const expiryDate = new Date()
    expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS)

    const cookieName = generateCookieName(blockId)
    document.cookie = `${cookieName}=${encodeURIComponent(cookieValue)}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`
  } catch (error) {
  }
}

export const loadFiltersFromCookie = (blockId) => {
  try {
    const cookieName = generateCookieName(blockId)
    const cookies = document.cookie.split(';')
    const filterCookie = cookies.find(cookie => cookie.trim().startsWith(`${cookieName}=`))

    if (filterCookie) {
      const cookieValue = filterCookie.split('=')[1]
      const filters = JSON.parse(decodeURIComponent(cookieValue))
      return filters
    }
  } catch (error) {
  }

  return null
}

export const clearFiltersCookie = (blockId) => {
  try {
    const cookieName = generateCookieName(blockId)
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
  } catch (error) {
  }
}

export const fetchResources = async ({ post_type, posts_per_page, page, search, filters, exclude_featured, terms, card_path }) => {
  try {
    const params = new URLSearchParams({
      post_type: post_type || 'post',
      posts_per_page: posts_per_page || 8,
      page: page || 1,
    })

    if (search) {
      params.append('query', search)
    }

    if (filters && Object.keys(filters).length > 0) {
      params.append('filters', JSON.stringify(filters))
    }

    if (exclude_featured !== undefined) {
      params.append('exclude_featured', exclude_featured ? '1' : '0')
    }

    if (terms && terms.length > 0) {
      params.append('terms', JSON.stringify(terms))
    }

    if (card_path) {
      params.append('card_path', card_path)
    }

    const response = await fetch(`${REST_BASE}/resources?${params.toString()}`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching resources:', error)
    throw error
  }
}

export const fetchResourcesHtml = async ({ post_type, posts_per_page, page, search, filters, exclude_featured, terms, card_path, widget, widget_size }) => {
  try {
    const params = new URLSearchParams({
      post_type: post_type || 'post',
      posts_per_page: posts_per_page || 8,
      page: page || 1,
    })

    if (search) {
      params.append('query', search)
    }

    if (filters && Object.keys(filters).length > 0) {
      params.append('filters', JSON.stringify(filters))
    }

    if (exclude_featured !== undefined) {
      params.append('exclude_featured', exclude_featured ? '1' : '0')
    }

    if (terms && terms.length > 0) {
      params.append('terms', JSON.stringify(terms))
    }

    if (card_path) {
      params.append('card_path', card_path)
    }

    if (widget) {
      params.append('widget', widget)
    }

    if (widget_size) {
      params.append('widget_size', widget_size)
    }

    const response = await fetch(`${REST_BASE}/resources/html?${params.toString()}`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching resources HTML:', error)
    throw error
  }
}

export const fetchSingleCard = async (postId, cardPath = 'template-parts/cards/insights') => {
  try {
    const params = new URLSearchParams({
      card_path: cardPath
    })

    const response = await fetch(`${REST_BASE}/resources/card/${postId}?${params.toString()}`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching single card:', error)
    throw error
  }
}

export const fetchValidOptions = async ({ post_type, filters }) => {
  try {
    const params = new URLSearchParams({
      post_type: post_type || 'post',
    })

    if (filters && Object.keys(filters).length > 0) {
      params.append('filters', JSON.stringify(filters))
    }

    const response = await fetch(`${REST_BASE}/resources/valid-options?${params.toString()}`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching valid options:', error)
    throw error
  }
}

export const fetchFilterTerms = async ({ post_type, terms }) => {
  try {
    const params = new URLSearchParams({
      post_type: post_type || 'post',
    })

    if (terms && terms.length > 0) {
      params.append('terms', JSON.stringify(terms))
    }

    const response = await fetch(`${REST_BASE}/resources/terms?${params.toString()}`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching filter terms:', error)
    throw error
  }
}

export const getAuthors = async (postType = 'post') => {
  try {
    const response = await fetch(`${REST_BASE}/resources/authors?post_type=${postType}`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.authors || []
  } catch (error) {
    console.error('Error fetching authors:', error)
    throw error
  }
}

export const syncAuthorRelationships = async () => {
  try {
    const response = await fetch(`${REST_BASE}/resources/sync-authors`, {
      method: 'POST',
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error syncing author relationships:', error)
    throw error
  }
}

export const syncAuthorRelationshipsBatch = async (postType = 'post', offset = 0, limit = 50) => {
  try {
    const params = new URLSearchParams({
      post_type: postType,
      offset: offset.toString(),
      limit: limit.toString()
    })

    const response = await fetch(`${REST_BASE}/resources/sync-authors-batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString()
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error batch syncing author relationships:', error)
    throw error
  }
}

export const searchAuthors = async (termName, searchTerm) => {
  try {
    const formData = new FormData()
    formData.append('action', 'search_authors')
    formData.append('security', NONCE)
    formData.append('term_name', termName)
    formData.append('search_term', searchTerm)

    const response = await fetch(AJAX_BASE, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    if (!data.success) {
      throw new Error(data.data?.message || 'Failed to search authors')
    }

    return data.data.authors
  } catch (error) {
    console.error('Error searching authors:', error)
    throw error
  }
}

export const fetchDiveDeeper = async ({ post_type, filters, terms }) => {
  try {
    const params = new URLSearchParams({
      post_type: post_type || 'post',
    })

    if (filters && Object.keys(filters).length > 0) {
      params.append('filters', JSON.stringify(filters))
    }

    if (terms && terms.length > 0) {
      params.append('terms', JSON.stringify(terms))
    }

    const response = await fetch(`${REST_BASE}/resources/dive-deeper?${params.toString()}`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching dive deeper data:', error)
    throw error
  }
}
